'use client';

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { getFreeGiftLines } from '@/actions/free-gift';
import { useCart } from '@/hooks';
import type { FreeGiftLine } from '@/lib/free-gift/types';

const FreeGiftLinesContext = createContext<Record<string, FreeGiftLine> | null>(null);

/**
 * Supplies gift state for cart lines.
 *
 * `is_free_gift` can't ride on the adapter's mapped `CartItem`, so gift lines are
 * looked up separately and keyed by item uid. The lookup re-runs whenever the
 * cart's uid/quantity signature changes, because the server recomputes how many
 * gift units are free every time the qualifying quantity moves.
 */
export function FreeGiftLinesProvider({ children }: { children: ReactNode }) {
  const { cart } = useCart();
  const [lines, setLines] = useState<Record<string, FreeGiftLine>>({});

  // Quantities matter, not just membership: lowering the qualifying quantity
  // changes the free/paid split without changing which lines exist.
  const signature = useMemo(
    () => (cart?.items ?? []).map((item) => `${item.uid}:${item.quantity}`).join(','),
    [cart],
  );

  useEffect(() => {
    if (!signature) {
      setLines({});
      return;
    }

    let active = true;
    getFreeGiftLines()
      .then((next) => {
        if (active) setLines(next);
      })
      .catch(() => {
        // A failed lookup only costs gift styling; the cart still renders.
      });

    return () => {
      active = false;
    };
  }, [signature]);

  return <FreeGiftLinesContext.Provider value={lines}>{children}</FreeGiftLinesContext.Provider>;
}

/**
 * The gift line for a cart item, or undefined when the item is not a gift.
 * Safe outside the provider — cart lines then simply render as normal items.
 */
export function useFreeGiftLine(uid: string): FreeGiftLine | undefined {
  return useContext(FreeGiftLinesContext)?.[uid];
}
